// Layer 2 of the fixture: REAL BYTES, FABRICATED EXPECTATIONS.
//
// NOTHING HERE IS VERIFICATION. Layer 1 (syntheticLedgerSource.ts) invents the
// bytes as well as the answer; this module hashes the actual media segments the
// player is streaming — a genuine SHA-256 over what arrived off the wire — and
// then fabricates the "sealed" hash to be whatever that digest turned out to
// be. Everything therefore passes, by construction and not by agreement. No
// manifest is fetched, no chain is read, and no comparison is ever made against
// anything a publisher signed. `warnOnce` (fixtureMode.ts) says so in the
// console once per page load, and the only reason this layer exists is that a
// recorded demo of the chart should march to the rhythm of real network
// arrivals — real jitter, real seeks, real ABR switches — rather than to a
// 250ms timer.
//
// WHY IT STILL HASHES FOR REAL. The digest is not decoration: it is what the
// chart prints above each column, and it is the one number in a fixture take
// that a viewer could independently reproduce from the segment file. Faking it
// would make the most photographed part of the demo the most fictional.
//
// THREE THINGS IT INHERITS FROM SegmentVerifier.ts, deliberately:
//
//  1. POSITION, NEVER URL (SegmentVerifier.ts:1). An arriving URL is only a
//     lookup key into the ORDER of the media playlist VHS is actually playing.
//     Nothing here compares a URL against anything else, so a re-hosted video
//     behaves identically.
//  2. `requestType === 'segment'` ONLY. Init segments, keys and playlists pass
//     through the same VHS hook and are not media bytes; they are ignored.
//  3. THE DIGEST CALL IS SYNCHRONOUS, BEFORE ANY `await`. VHS may transfer the
//     same ArrayBuffer to its transmuxer worker, which detaches it. See the
//     note on `handleSegmentResponse` below.
//
// WHERE IT DIVERGES: a failed, aborted or unreadable request emits NOTHING AT
// ALL — not even the `kind: 'network'` result the real verifier reports. The
// real path reports those because a policy above it needs to know it stopped
// being able to check; a fixture has no such consumer, and the one thing a demo
// must never do is show a viewer a failure that nothing real is behind.
import { describeMappingObstacle } from '../SegmentVerifier.js'
import type {
  ParsedPlaylist,
  SegmentVerificationResult,
  VerifiablePlayer,
  VhsRequest,
  VhsResponseHook,
  VhsXhr,
} from '../SegmentVerifier.js'
import { sha256Hex } from '../hash.js'
import { fakeHashHex } from '../useSimulatedVerification.js'
import { SYNTHETIC_RENDITION_NAME, SYNTHETIC_SLICE_SECONDS, syntheticSegmentCount } from './syntheticLedgerSource.js'

// Restated from SegmentVerifier.ts, where it is module-private. Not exported
// from there on purpose: this file is a fixture and must not be a reason to
// widen the real verifier's surface. The value is VHS's own tag for a media
// segment fetch (@videojs/http-streaming's segment-loader).
const MEDIA_SEGMENT_REQUEST_TYPE = 'segment'

// Same throttle, and for the same reason: on a demuxed ladder an audio segment
// arrives for every video segment and none of them is in the index, so an
// unthrottled re-read would walk VHS's playlist list on the main thread next to
// the 360° render loop.
const PLAYLIST_REFRESH_INTERVAL_MS = 1000

// Guards the cell-overlap arithmetic below against floating-point cumulative
// sums: a segment ending at exactly 8.0 must not be treated as touching the
// cell that starts at 8.0.
const CELL_EPSILON_SECONDS = 1e-6

/** What one playlist position occupies on the video's timeline, recovered from
 * the playlist's own EXTINF order. This is the whole of what a URL buys us. */
interface SegmentSpan {
  start: number
  duration: number
}

export interface RealLedgerSourceOptions {
  /** Only ever a seed for the fabricated "sealed" hash on the scripted
   * mismatch, so that a re-take of a recording shows the same fake hash. */
  videoId: string
  /** The player's own duration, which fixes how many cells the fixture ledger
   * has. Must match what `buildSyntheticRoot` was given, or results will name
   * cells the lane does not have and be dropped by `applyResult`. */
  duration: number
  /** Fixture cell width. Must match the ledger's. */
  slice?: number
  /** Lane the results are attributed to. Must be a lane the ledger has —
   * `buildSyntheticRoot` builds exactly one, named SYNTHETIC_RENDITION_NAME. */
  rendition?: string
  /** Fixture cell index that mismatches, or null to never fail. */
  failSlice?: number | null
  /** Every outcome leaves here. May throw; it is caught, so a consumer cannot
   * unwind through VHS's xhr callback and take the segment loader down. */
  onResult: (result: SegmentVerificationResult) => void
}

/** Mirrors `SegmentVerifier`'s attach/detach contract so the two are
 * interchangeable at the call site — see `attachRealLedgerSource`. */
export interface RealLedgerSource {
  attach: (player: VerifiablePlayer) => void
  detach: () => void
  /**
   * Hash and report one segment response. Public for the same reason
   * `SegmentVerifier.handleSegmentResponse` is: it is the entire behaviour of
   * this module with the VHS plumbing taken away, so it can be driven by hand.
   * The VHS hook deliberately drops the returned promise.
   */
  handleSegmentResponse: (input: {
    uri: string
    bytes: ArrayBuffer | null
    failed?: boolean
    aborted?: boolean
  }) => Promise<void>
}

/**
 * A source of `SegmentVerificationResult`s driven by real segment arrivals.
 *
 * A DROP-IN FOR `createSyntheticLedgerSource`, with one difference in shape
 * that follows from where the results come from: the synthetic source is
 * pulled (`advance(currentTime)` returns what is new), this one is pushed
 * (`onResult` fires whenever bytes land). There is no clock to advance,
 * because the network is the clock.
 *
 * PROJECTION ONTO THE FIXTURE GRID, stated plainly because it is the one
 * non-obvious decision in this file. The fixture ledger's lane is fabricated
 * from `duration` in fixed `slice`-second cells (syntheticLedgerSource.ts); a
 * real ladder's segments are whatever length the packager chose, on whichever
 * quality level ABR picked. So a real arrival is mapped: URL → playlist
 * POSITION (rule 1 above) → the span of seconds that position occupies →
 * every fixture cell that span overlaps. A real 6s segment therefore lights two
 * 4s cells and a real 2s segment lights one, and the chart never shows a hole
 * where bytes actually arrived. The alternative — emitting the raw playlist
 * position — would need lanes built from the real ladder, which means reading
 * manifests, which this layer explicitly does not do.
 *
 * CONSEQUENCE, ACCEPTED: quality switches show up in this mode as real timing
 * and real jitter, but not as separate lanes or handover hairlines, because the
 * fixture ledger has exactly one lane to record into. Segments from every level
 * land in it. See the Discovered Issues Log entry for T13.
 */
export function createRealLedgerSource(opts: RealLedgerSourceOptions): RealLedgerSource {
  const slice = opts.slice ?? SYNTHETIC_SLICE_SECONDS
  const rendition = opts.rendition ?? SYNTHETIC_RENDITION_NAME
  const failSlice = opts.failSlice ?? null
  const cellCount = syntheticSegmentCount(opts.duration, slice)

  /** Segment URL → its span, built from playlist ORDER. The URL is a lookup
   * key and nothing else. */
  const spans = new Map<string, SegmentSpan>()
  /** `<uri>#<segmentCount>`, so a reload of the same playlist is free and a
   * genuinely different one is re-read. */
  const notedPlaylists = new Set<string>()
  /** Fixture cells already reported. A seek re-fetches segments that already
   * passed; re-hashing them is nearly free but re-announcing them would spray
   * duplicates at the ledger. */
  const reported = new Set<number>()
  const hookedXhrs = new Set<VhsXhr>()

  let player: VerifiablePlayer | null = null
  let detached = false
  /** Set by the scripted mismatch and never cleared: after a mismatch this
   * source is finished for good, matching `LaneSet.mismatch` being sticky and
   * matching the synthetic source's `halted`. */
  let halted = false
  let lastPlaylistRefresh = 0

  function emit(result: SegmentVerificationResult): void {
    if (detached) return
    try {
      opts.onResult(result)
    } catch {
      // A consumer that throws must not unwind through VHS's xhr callback.
    }
  }

  function vhs(): { xhr?: VhsXhr; playlists?: unknown } | null {
    if (!player) return null
    try {
      const tech = player.tech({ IWillNotUseThisInPlugins: true }) as
        | { vhs?: { xhr?: VhsXhr; playlists?: unknown } }
        | null
      return tech?.vhs ?? null
    } catch {
      // No tech, or a native HLS playback path (Safari/iOS), where there is no
      // loader to hook and so nothing to hash. A real gap in the real path; in
      // fixture mode it just means this layer stays silent.
      return null
    }
  }

  /**
   * Take the segment ORDER out of one parsed media playlist, and with it each
   * position's span on the timeline.
   *
   * Structure only — never the `.m3u8` text. A CDN may legitimately regenerate
   * a playlist with different whitespace, so nothing here reads bytes of it.
   */
  function notePlaylist(playlist: ParsedPlaylist): void {
    const segments = playlist.segments ?? []
    // Known to VHS but not loaded yet; the signature lets it be re-read once
    // its segments arrive.
    if (segments.length === 0) return
    const key = playlist.resolvedUri || playlist.uri
    if (!key) return
    const signature = `${key}#${segments.length}`
    if (notedPlaylists.has(signature)) return
    notedPlaylists.add(signature)

    // Reused rather than restated: a byterange playlist or a repeated segment
    // URL makes a position unrecoverable from a request, which is exactly the
    // question this helper answers for the real verifier.
    if (describeMappingObstacle(segments)) return

    // Durations are load-bearing here in a way they are not for the real
    // verifier — they ARE the mapping — so a playlist with any unusable EXTINF
    // is skipped whole rather than indexed with a drifting cumulative sum.
    for (const segment of segments) {
      const d = segment.duration
      if (typeof d !== 'number' || !Number.isFinite(d) || d <= 0) return
    }

    let start = 0
    for (const segment of segments) {
      const uri = segment.resolvedUri || segment.uri
      const duration = segment.duration as number
      if (uri) spans.set(uri, { start, duration })
      start += duration
    }
  }

  /** Re-read VHS's parsed playlists. Cheap after the first time — notePlaylist
   * early-returns on a signature it has already seen. */
  function refreshPlaylists(): void {
    const now = Date.now()
    if (now - lastPlaylistRefresh < PLAYLIST_REFRESH_INTERVAL_MS) return
    lastPlaylistRefresh = now
    const playlists = vhs()?.playlists as
      | {
          main?: { playlists?: ParsedPlaylist[] }
          master?: { playlists?: ParsedPlaylist[] }
          media?: () => ParsedPlaylist | undefined
        }
      | undefined
    if (!playlists) return
    try {
      // `main` since VHS 2.15, `master` before it. Only the video variants live
      // here; audio renditions of a demuxed ladder sit under `mediaGroups` and
      // are deliberately not indexed, so audio bytes never light a cell.
      const main = playlists.main ?? playlists.master
      for (const playlist of main?.playlists ?? []) notePlaylist(playlist)
      const active = playlists.media?.()
      if (active) notePlaylist(active)
    } catch {
      // VHS's internal shapes differ across releases; a playlist we cannot read
      // just means those segments light nothing.
    }
  }

  /** Every fixture cell the span overlaps, ascending, clamped to the lane. */
  function cellsCovered(span: SegmentSpan): number[] {
    if (cellCount === 0) return []
    const first = Math.floor((span.start + CELL_EPSILON_SECONDS) / slice)
    const last = Math.ceil((span.start + span.duration - CELL_EPSILON_SECONDS) / slice) - 1
    const cells: number[] = []
    for (let index = Math.max(0, first); index <= Math.min(cellCount - 1, last); index++) {
      cells.push(index)
    }
    return cells
  }

  /**
   * ORDERING IS LOAD-BEARING, exactly as in SegmentVerifier: everything above
   * the `sha256Hex` call is synchronous, and `crypto.subtle.digest` copies the
   * bytes at call time. VHS hands the same ArrayBuffer to its transmuxer worker
   * and may TRANSFER it, detaching the buffer — so an `await` inserted above
   * that line would leave this hashing a zero-length corpse. Do not add one.
   */
  async function handleSegmentResponse(input: {
    uri: string
    bytes: ArrayBuffer | null
    failed?: boolean
    aborted?: boolean
  }): Promise<void> {
    if (detached || halted) return
    // An aborted request is routine (ABR switches, seeks) and a failed one is a
    // CDN blip. Neither is evidence of anything, and neither may ever surface
    // as a mismatch — so both simply produce nothing.
    if (input.aborted || input.failed) return

    let span = spans.get(input.uri)
    if (!span) {
      // The playlist this segment came from may have loaded since the last
      // look. Throttled — see PLAYLIST_REFRESH_INTERVAL_MS.
      refreshPlaylists()
      span = spans.get(input.uri)
    }
    if (!span) return

    const body = input.bytes
    if (!(body instanceof ArrayBuffer)) return
    // A detached (transferred) buffer reads as zero-length and would hash to
    // the digest of the empty string — a stable, entirely meaningless number
    // that would then be printed on a column as if it came from the video.
    if (body.byteLength === 0) return

    // Synchronous call, awaited afterwards. See the note above. The length is
    // read here for the same reason: after the await the buffer may be detached.
    const digest = sha256Hex(body)
    let bytes: number | undefined = body.byteLength

    let actualHash: string
    try {
      actualHash = await digest
    } catch {
      // Unreadable bytes are not changed bytes.
      return
    }
    if (detached || halted) return

    for (const index of cellsCovered(span)) {
      if (reported.has(index)) continue
      reported.add(index)

      if (index === failSlice) {
        halted = true
        emit({
          ok: false,
          stage: 'segment',
          kind: 'mismatch',
          detail: `Fixture-scripted mismatch at slice ${index}`,
          rendition,
          segmentIndex: index,
          // The genuinely computed digest, reported as what arrived — because
          // it is — against a fabricated "sealed" hash it could never equal.
          // Deterministic in `videoId` and `index`, so two takes of the same
          // recording show the same fake.
          expectedHash: fakeHashHex(`fixture-segment-sealed:${opts.videoId}:${index}`),
          actualHash,
        })
        return
      }

      emit({
        ok: true,
        stage: 'segment',
        rendition,
        segmentIndex: index,
        // The expectation is fabricated to equal the computation, which is the
        // whole trick of this layer: the hash is real, the agreement is not.
        hash: actualHash,
        // One segment can cover several cells; its bytes are counted once.
        bytes,
      })
      bytes = undefined
    }
  }

  const responseHook: VhsResponseHook = (request: VhsRequest, error: unknown) => {
    if (detached || halted) return
    if (request?.requestType !== MEDIA_SEGMENT_REQUEST_TYPE) return
    const uri = request.uri
    if (!uri) return
    // Deliberately dropped: this runs on the main thread and must return before
    // the digest resolves.
    void handleSegmentResponse({
      uri,
      bytes: (request.response ?? null) as ArrayBuffer | null,
      failed: Boolean(error),
      aborted: Boolean(request.aborted),
    })
  }

  function registerResponseHook(): void {
    if (detached) return
    const xhr = vhs()?.xhr
    if (!xhr || typeof xhr.onResponse !== 'function') return
    if (hookedXhrs.has(xhr)) return
    hookedXhrs.add(xhr)
    try {
      xhr.onResponse(responseHook)
    } catch {
      hookedXhrs.delete(xhr)
    }
  }

  const hooksReadyListener = () => registerResponseHook()

  return {
    /**
     * VHS builds a fresh handler — and a fresh xhr object — for every source,
     * announcing each with `xhr-hooks-ready`. PlaybackCore keeps one player for
     * the whole mount and swaps streams through `setSource`, so binding once
     * would go quiet after the first sphere <-> quadrant crossing; hence the
     * listener as well as the immediate attempt. Registration is idempotent:
     * VHS keeps its hooks in a Set and this passes the same function each time.
     */
    attach(nextPlayer: VerifiablePlayer): void {
      player = nextPlayer
      detached = false
      try {
        nextPlayer.on('xhr-hooks-ready', hooksReadyListener)
      } catch {
        // A player that cannot take listeners gives us whatever the immediate
        // registration below finds, and nothing after that.
      }
      registerResponseHook()
    },

    /** Stop hashing and stop reporting — including from digests already in
     * flight, whose results would otherwise land after unmount. */
    detach(): void {
      detached = true
      const current = player
      player = null
      try {
        current?.off('xhr-hooks-ready', hooksReadyListener)
      } catch {
        // player already disposed
      }
      for (const xhr of hookedXhrs) {
        try {
          xhr.offResponse?.(responseHook)
        } catch {
          // tech already disposed
        }
      }
      hookedXhrs.clear()
    },

    handleSegmentResponse,
  }
}

/**
 * Attach a real-bytes fixture source to a player, or — when there is none — do
 * NOTHING.
 *
 * The same shape as `attachSegmentVerifier` (SegmentVerifier.ts), and for the
 * same reason: with no source supplied the player object is not read, not
 * listened to and not reached into, so an ordinary page load cannot be affected
 * by this file existing. Returns a teardown for React's effect cleanup, or
 * undefined when there was nothing to tear down.
 */
export function attachRealLedgerSource(
  source: RealLedgerSource | null | undefined,
  player: VerifiablePlayer,
): (() => void) | undefined {
  if (!source) return undefined
  source.attach(player)
  return () => source.detach()
}
